// follower_stats exposes only top-level status COUNTS, not per-index state
// (syncing/paused/failed) or leader_alias/leader_index — those require per-index
// _plugins/_replication/<index>/_status calls (deferred).

const CHECKPOINT_UNSET = -1;

const toNum = (v: any): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

const toCount = (v: any): number => {
  const n = toNum(v);
  return n !== null && n >= 0 ? n : 0;
};

// -1 means the checkpoint is not yet established → treat as unknown (null).
const normalizeCheckpoint = (v: any): number | null => {
  const n = toNum(v);
  if (n === null || n === CHECKPOINT_UNSET) return null;
  return n;
};

export interface IndexReplicationRow {
  indexName: string;
  followerCheckpoint: number | null;
  leaderCheckpoint: number | null;
  checkpointLag: number | null;
  operationsWritten: number;
  failedRequests: number;
}

export interface ReplicationSummary {
  syncing: number;
  paused: number;
  failed: number;
  bootstrapping: number;
}

export const parseIndexReplication = (followerStats: any): IndexReplicationRow[] => {
  if (!followerStats || typeof followerStats !== 'object') return [];
  const idx = followerStats.index_stats;
  if (!idx || typeof idx !== 'object' || Array.isArray(idx)) return [];

  return Object.keys(idx).map((indexName) => {
    const s = idx[indexName] && typeof idx[indexName] === 'object' ? idx[indexName] : {};
    const followerCheckpoint = normalizeCheckpoint(s.follower_checkpoint);
    const leaderCheckpoint = normalizeCheckpoint(s.leader_checkpoint);
    const checkpointLag =
      followerCheckpoint !== null && leaderCheckpoint !== null
        ? leaderCheckpoint - followerCheckpoint
        : null;

    return {
      indexName,
      followerCheckpoint,
      leaderCheckpoint,
      checkpointLag,
      operationsWritten: toCount(s.operations_written),
      failedRequests: toCount(s.failed_read_requests) + toCount(s.failed_write_requests),
    };
  });
};

export const getReplicationSummary = (followerStats: any): ReplicationSummary => {
  const count = (key: string): number =>
    followerStats && typeof followerStats === 'object' ? toCount(followerStats[key]) : 0;
  return {
    syncing: count('num_syncing_indices'),
    paused: count('num_paused_indices'),
    failed: count('num_failed_indices'),
    bootstrapping: count('num_bootstrapping_indices'),
  };
};
